import { Check } from "lucide-react";

import { MockGenieConfig } from "@/types/mockups/genie-setup-v2";

import { knowledgeSources } from "../data";

interface Step4KnowledgeProps {
  config: MockGenieConfig;
  onChange: (config: MockGenieConfig) => void;
}

export const Step4Knowledge = ({ config, onChange }: Step4KnowledgeProps) => {
  const toggleSource = (sourceId: string) => {
    const exists = config.knowledge.sources.includes(sourceId);
    const next = exists
      ? config.knowledge.sources.filter((id) => id !== sourceId)
      : [...config.knowledge.sources, sourceId];
    onChange({
      ...config,
      knowledge: { ...config.knowledge, sources: next },
    });
  };

  const handleContextChange = (val: string) => {
    onChange({
      ...config,
      knowledge: { ...config.knowledge, customContext: val },
    });
  };

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Connect knowledge sources</h3>
        <p className="text-xs text-gray-500 mb-4">
          Your genie answers from these first. Add as many as you have.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {knowledgeSources.map((source) => {
            const Icon = source.icon;
            const isSelected = config.knowledge.sources.includes(source.id);
            return (
              <button
                key={source.id}
                type="button"
                onClick={() => toggleSource(source.id)}
                className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? "border-genie-primary bg-genie-primary/5"
                    : "border-gray-200 bg-white hover:border-genie-primary/40"
                }`}
              >
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-genie-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="flex items-start gap-3 pr-6">
                  <div className="w-9 h-9 rounded-lg bg-genie-primary/10 text-genie-primary flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">{source.label}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{source.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">
          Anything else your genie should know
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Plain language. The stuff only you know — your business, your voice, what to avoid.
        </p>
        <textarea
          value={config.knowledge.customContext}
          onChange={(e) => handleContextChange(e.target.value)}
          rows={6}
          placeholder="Example: We sell custom orthotics for runners. Always recommend a fitting before purchase. Never quote a price below $180. If someone mentions pain, suggest they see a podiatrist first."
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-sm resize-y"
        />
        <p className="text-xs text-gray-400 mt-2">
          {config.knowledge.customContext.length} characters
        </p>
      </section>
    </div>
  );
};
